import { format } from 'date-fns';
import XLSX from 'xlsx';
import userSlice from 'app/store/user/user.slice';
import { BudgetType } from 'app/types/budget/budget.types';

type ExportCell = string | number | null | undefined;

export const useExportBudgetData = () => {
    const { user } = userSlice();

    return ({
        data,
        includeHeadings,
        includeRegistered,
        includeVariance,
    }: {
        data: BudgetType;
        includeHeadings: boolean;
        includeRegistered: boolean;
        includeVariance: boolean;
    }) => {
        const exportedAt = new Date();
        const currency = user.location?.ccy || '';
        const currencyFormat = `#,##0.00 "${currency}";[Red]-#,##0.00 "${currency}";"–"`;
        const workbook = XLSX.utils.book_new();

        const addSheet = (
            name: string,
            rows: ExportCell[][],
            currencyColumns: number[],
            headerRow = 0,
            filterEndRow = rows.length - 1
        ) => {
            const sheet = XLSX.utils.aoa_to_sheet(rows);
            sheet['!cols'] = rows[headerRow].map((_, column) => ({
                wch: Math.min(
                    64,
                    rows.reduce(
                        (width, row) => Math.max(width, String(row[column] ?? '').length + 2),
                        14
                    )
                ),
            }));
            for (let row = headerRow + 1; row < rows.length; row += 1) {
                rows[row].forEach((value, column) => {
                    if (typeof value === 'number') {
                        const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })];
                        cell.z = currencyColumns.includes(column) ? currencyFormat : '#,##0.##';
                    }
                });
            }
            sheet['!autofilter'] = {
                ref: XLSX.utils.encode_range({
                    s: { r: headerRow, c: 0 },
                    e: { r: filterEndRow, c: rows[headerRow].length - 1 },
                }),
            };
            XLSX.utils.book_append_sheet(workbook, sheet, name);
            return sheet;
        };

        const financialRows: ExportCell[][] = [
            [
                'Total revenues',
                ...(includeRegistered ? [+data.total_revenues_curr] : []),
                +data.total_revenues_proj,
            ],
            [
                'Total costs',
                ...(includeRegistered ? [+data.total_costs_curr] : []),
                +data.total_costs_proj,
            ],
            ['Net result', ...(includeRegistered ? [+data.total_curr] : []), +data.total_proj],
        ];
        addSheet(
            'Summary',
            [
                ['Budget Builder — budget snapshot'],
                ['Race', data.race.name],
                ['Race date', data.race.start_date],
                ['Exported at', format(exportedAt, 'yyyy-MM-dd HH:mm:ss XXX')],
                ['Currency', currency],
                [
                    'Values are a snapshot. Editing this workbook does not recalculate the budget or update RDHQ.',
                ],
                ['Costs are negative; net result is revenues plus costs.'],
                ['Projected totals use actual prices where supplied, otherwise budgeted prices.'],
                ['Measure', ...(includeRegistered ? ['Registered'] : []), 'Projected'],
                ...financialRows,
            ],
            includeRegistered ? [1, 2] : [1],
            8
        );

        const detailHeaders = [
            'Type',
            ...(includeHeadings ? ['Group'] : []),
            'Item',
            'Event',
            'Budgeted unit price',
            'Actual unit price',
            'Price used',
            'Price status',
            'Quantity basis',
            ...(includeRegistered ? ['Registered quantity', 'Registered total'] : []),
            'Projected quantity',
            'Projected total',
            'Comments',
        ];
        const quantityLabels: Record<string, string> = {
            participant: 'Per participant',
            event: 'Per event',
            custom: 'Custom',
        };
        const detailRows: ExportCell[][] = data.items.map((item) => [
            item.category?.type,
            ...(includeHeadings ? [item.category?.group] : []),
            item.name_override || item.category?.name,
            item.event === null ? 'All events' : item.event.name,
            +item.price,
            item.actualized ? +item.actual_price : null,
            item.actualized ? +item.actual_price : +item.price,
            item.actualized ? 'Actual' : 'Budgeted',
            quantityLabels[item.quantity_type],
            ...(includeRegistered ? [+item.quantity_curr, +item.total_curr] : []),
            +item.quantity_proj,
            +item.total_proj,
            item.comments,
        ]);
        const totalsRows = financialRows.map(([label, ...amounts]) => {
            const row: ExportCell[] = Array(detailHeaders.length).fill(null);
            row[detailHeaders.indexOf('Item')] = label;
            if (includeRegistered) [row[detailHeaders.indexOf('Registered total')]] = amounts;
            row[detailHeaders.indexOf('Projected total')] = amounts[amounts.length - 1];
            return row;
        });
        addSheet(
            'Budget detail',
            [detailHeaders, ...detailRows, [], ...totalsRows],
            detailHeaders.flatMap((header, index) =>
                [
                    'Budgeted unit price',
                    'Actual unit price',
                    'Price used',
                    'Registered total',
                    'Projected total',
                ].includes(header)
                    ? [index]
                    : []
            ),
            0,
            detailRows.length
        );

        const eventHeaders = [
            'Event',
            ...(includeRegistered ? ['Registered participants'] : []),
            'Projected participants',
            'Average entry fee',
        ];
        const eventRows = data.events.map((event) => [
            event.name,
            ...(includeRegistered ? [+event.participants_curr] : []),
            +event.participants_proj,
            event.avg_entry_fee === null ? null : +event.avg_entry_fee,
        ]);
        const scheduleHeaders = [
            'Event',
            'Schedule date',
            'Entry fee',
            ...(includeRegistered ? ['Registered participants'] : []),
            'Projected participants',
        ];
        const scheduleRows = data.events.flatMap((event) =>
            event.nodes.map((node) => [
                event.name,
                node.date,
                +node.entry_fee,
                ...(includeRegistered ? [+node.participants_curr] : []),
                +node.participants_proj,
            ])
        );
        const eventsSheet = addSheet(
            'Events and assumptions',
            [eventHeaders, ...eventRows],
            [eventHeaders.indexOf('Average entry fee')]
        );
        const scheduleStart = eventRows.length + 4;
        XLSX.utils.sheet_add_aoa(
            eventsSheet,
            [['Entry fee and participant schedule'], scheduleHeaders, ...scheduleRows],
            { origin: { r: scheduleStart, c: 0 } }
        );
        scheduleRows.forEach((_, index) => {
            eventsSheet[XLSX.utils.encode_cell({ r: scheduleStart + 2 + index, c: 2 })].z =
                currencyFormat;
        });
        eventsSheet['!cols'] = scheduleHeaders.map((header, index) => ({
            wch: Math.max(header.length + 2, index === 0 ? 40 : 26),
        }));

        if (includeVariance) {
            const varianceHeaders = [
                'Type',
                ...(includeHeadings ? ['Group'] : []),
                'Item',
                'Event',
                'Price status',
                'Budgeted total',
                'Projected total',
                'Variance',
                'Variance percent',
            ];
            addSheet(
                'Variance',
                [
                    varianceHeaders,
                    ...data.items
                        .filter((item) => item.editable)
                        .map((item) => [
                            item.category?.type,
                            ...(includeHeadings ? [item.category?.group] : []),
                            item.name_override || item.category?.name,
                            item.event === null ? 'All events' : item.event.name,
                            item.actualized ? 'Actual' : 'Budgeted',
                            Number(item.total_budg),
                            +item.total_proj,
                            item.variance == null ? null : +item.variance,
                            item.variance_pct,
                        ]),
                ],
                ['Budgeted total', 'Projected total', 'Variance'].map((header) =>
                    varianceHeaders.indexOf(header)
                )
            );
        }

        XLSX.writeFile(
            workbook,
            `Budget Builder - ${data.race.name} - ${format(exportedAt, 'yyyy-MM-dd')}.xlsx`,
            { compression: true }
        );
    };
};
